import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  CreateNoteInput,
  Note,
  NoteListQuery,
  UpdateNoteInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['notes'] as const;

export interface NotePage {
  items: Note[];
  meta: CollectionMeta;
}

function toQuery(q: NoteListQuery): Record<string, string | number | boolean | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    contactId: q.contactId,
    leadId: q.leadId,
    conversationId: q.conversationId,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listNotes(q: NoteListQuery): Promise<NotePage> {
  const res = await apiClient.getCollection<Note>('/notes', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}
export const createNote = (input: CreateNoteInput) => apiClient.post<Note>('/notes', input);
export const updateNote = (id: string, patch: UpdateNoteInput) =>
  apiClient.patch<Note>(`/notes/${id}`, patch);
export const deleteNote = (id: string) => apiClient.delete<void>(`/notes/${id}`);

export function useNotes(query: NoteListQuery) {
  return useQuery<NotePage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listNotes(query),
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation<Note, ApiClientError, CreateNoteInput>({
    mutationFn: createNote,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation<Note, ApiClientError, { id: string; body: string }>({
    mutationFn: ({ id, body }) => updateNote(id, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteNote,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
